import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AxiosUrl from '../AxiosUrl';
import BkashCreatePaymentAPICall from '../../action/BkashCreatePaymentAPICall';
import styles from './GetStarted.module.css';

// Launch offer id — must match SystemBilling/config/launchOffer.js on the backend.
const OFFER_ID = 'launch-1000';

// Optional promo video. Leave empty to show the placeholder; drop in a YouTube
// embed / Facebook / MP4 URL later and it renders automatically.
const VIDEO_URL = '';

// Latin → Bangla digits for display polish.
const bn = (v) => String(v).replace(/[0-9]/g, (d) => '০১২৩৪৫৬৭৮৯'[d]);
const pad2 = (n) => String(Math.max(0, n)).padStart(2, '0');

// Store-name helpers (mirror SetStoreName): clean to a subdomain + validate.
const cleanStoreName = (raw) =>
    (raw || '')
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^e-/, '')
        .substring(0, 28);

const validateStore = (name) => {
    if (name.length < 2) return 'স্টোরের নাম অন্তত ২ অক্ষরের হতে হবে';
    if (name.length > 28) return 'স্টোরের নাম ২৮ অক্ষরের কম হতে হবে';
    if (!/^[a-z0-9-]+$/.test(name)) return 'শুধু ছোট হাতের অক্ষর, সংখ্যা ও হাইফেন ব্যবহার করুন';
    if (name.startsWith('-') || name.endsWith('-')) return 'শুরুতে বা শেষে হাইফেন ব্যবহার করা যাবে না';
    return '';
};

function GetStarted() {
    const navigate = useNavigate();
    const uid = localStorage.getItem('id');

    const [loading, setLoading] = useState(true);       // initial status fetch
    const [paying, setPaying] = useState(false);         // bKash handoff in progress
    const [expired, setExpired] = useState(false);       // offer window closed
    const [price, setPrice] = useState(null);            // price preview payload
    const [deadline, setDeadline] = useState(null);      // offerExpiresAt (ms)
    const [remaining, setRemaining] = useState(0);       // ms left

    // Free store creation
    const [storeName, setStoreName] = useState('');
    const [creating, setCreating] = useState(false);
    const [createdSub, setCreatedSub] = useState(
        localStorage.getItem('createdStoreSub') || null
    ); // full subdomain, e.g. "e-myshop"
    const [showPreview, setShowPreview] = useState(false);
    const [copied, setCopied] = useState(false);

    // --- Show a payment error returned from the bKash callback --------------
    useEffect(() => {
        let msg = null;
        try {
            msg = sessionStorage.getItem('payError');
        } catch (e) {
            /* ignore */
        }
        // URL-param fallback (private mode / blocked sessionStorage).
        const params = new URLSearchParams(window.location.search);
        if (!msg) msg = params.get('payError');
        // Always strip the param from the URL so a refresh doesn't re-show it.
        if (params.has('payError')) {
            params.delete('payError');
            const qs = params.toString();
            window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
        }
        if (msg) {
            toast.error(msg);
            try {
                sessionStorage.removeItem('payError');
            } catch (e) {
                /* ignore */
            }
        }
    }, []);

    // --- Load subscription status + offer price -----------------------------
    useEffect(() => {
        let alive = true;
        if (!uid) {
            navigate('/login');
            return;
        }

        (async () => {
            try {
                // Already paid? Skip the offer and continue onboarding.
                const sub = await AxiosUrl.get(`/get-subscription/${uid}`);
                if (!alive) return;

                const isPaid = sub.data?.IsPaid === true;
                localStorage.setItem('IsPaid', String(isPaid));
                localStorage.setItem('offerActive', String(sub.data?.offerActive === true));

                if (isPaid) {
                    const createStore = localStorage.getItem('CreateStore');
                    const needsStore =
                        createStore === 'false' || createStore === 'False' || createStore === 'FALSE';
                    navigate(needsStore ? `/set-store-name/${uid}` : '/dashboard', { replace: true });
                    return;
                }

                if (sub.data?.offerExpiresAt) {
                    setDeadline(new Date(sub.data.offerExpiresAt).getTime());
                }

                // Backend is the source of truth for whether a store exists.
                if (sub.data?.hasStore && sub.data?.storeSubdomain) {
                    localStorage.setItem('createdStoreSub', sub.data.storeSubdomain);
                    setCreatedSub(sub.data.storeSubdomain);
                } else {
                    // No store (or it was deleted) — clear any stale local flag
                    // so the create form shows again.
                    localStorage.removeItem('createdStoreSub');
                    setCreatedSub(null);
                }

                // Server-validated offer price (৳1,000 / 1 year).
                try {
                    const pr = await AxiosUrl.post(`/price-preview/${uid}`, { offer: OFFER_ID });
                    if (!alive) return;
                    if (pr.data?.success) {
                        setPrice(pr.data.data);
                    } else if (pr.data?.offerExpired) {
                        setExpired(true);
                    }
                } catch (e) {
                    // If the offer preview fails, fall back to the expired view.
                    setExpired(true);
                }
            } catch (e) {
                console.error('GetStarted status error:', e);
            } finally {
                if (alive) setLoading(false);
            }
        })();

        return () => {
            alive = false;
        };
    }, [uid, navigate]);

    // --- Countdown ----------------------------------------------------------
    useEffect(() => {
        if (!deadline) return;
        const tick = () => {
            const left = deadline - Date.now();
            setRemaining(left);
            if (left <= 0) setExpired(true);
        };
        tick();
        const t = setInterval(tick, 1000);
        return () => clearInterval(t);
    }, [deadline]);

    const totalSec = Math.max(0, Math.floor(remaining / 1000));
    const hh = Math.floor(totalSec / 3600);
    const mm = Math.floor((totalSec % 3600) / 60);
    const ss = totalSec % 60;

    // --- Buy ----------------------------------------------------------------
    const handleBuy = useCallback(async () => {
        if (!uid || paying) return;
        setPaying(true);
        try {
            const res = await AxiosUrl.post(`/pay-bill/${uid}`, { offer: OFFER_ID });
            if (res.data?.success) {
                // Remember where to return the user if the bKash payment fails.
                localStorage.setItem('payReturn', '/get-started');
                // pendingPackage + amount are set server-side; hand off to bKash.
                BkashCreatePaymentAPICall(res.data.data.Amount, res.data.data.UID, res.data.data.UID);
            } else if (res.data?.offerExpired) {
                setExpired(true);
                setPaying(false);
                toast.info('অফারের সময় শেষ হয়ে গেছে। সব প্ল্যান দেখুন।');
            } else {
                setPaying(false);
                toast.error(res.data?.error || 'পেমেন্ট শুরু করা যায়নি');
            }
        } catch (e) {
            setPaying(false);
            const msg = e.response?.data?.error || 'পেমেন্ট শুরু করা যায়নি। আবার চেষ্টা করুন।';
            toast.error(msg);
        }
    }, [uid, paying]);

    // --- Free store creation ------------------------------------------------
    const storeSub = cleanStoreName(storeName);
    const storeMsg = storeName ? validateStore(storeSub) : '';
    const storeValid = !!storeName && !storeMsg;
    const previewSub = storeSub.startsWith('e-') ? storeSub : `e-${storeSub}`;

    const handleCreateStore = useCallback(async () => {
        if (!uid || creating) return;
        const sub = cleanStoreName(storeName);
        if (!storeName.trim() || validateStore(sub)) {
            toast.error(validateStore(sub) || 'আপনার স্টোরের নাম লিখুন');
            return;
        }
        setCreating(true);
        try {
            const res = await AxiosUrl.post(`/add-domain-new/${uid}`, {
                storeName: storeName.trim(),
            });
            if (res.status === 200 && res.data?.success) {
                const s = res.data.subdomain; // full "e-xxx"
                localStorage.setItem('CreateStore', 'true');
                localStorage.setItem('storeName', storeName);
                localStorage.setItem('createdStoreSub', s);
                setCreatedSub(s);
                setShowPreview(true);
                toast.success('স্টোর তৈরি হয়েছে!');
            } else {
                toast.error(res.data?.message || res.data?.error || 'স্টোর তৈরিতে সমস্যা হয়েছে');
            }
        } catch (e) {
            const msg =
                e.response?.data?.message ||
                (typeof e.response?.data === 'string' ? e.response.data : null) ||
                e.response?.data?.error ||
                'স্টোর তৈরিতে সমস্যা হয়েছে';
            toast.error(msg);
        } finally {
            setCreating(false);
        }
    }, [uid, storeName, creating]);

    const handleCopyStore = useCallback(async () => {
        const link = `https://${createdSub}.ecomtechbd.com`;
        const done = () => {
            setCopied(true);
            toast.success('স্টোর লিংক কপি হয়েছে');
            setTimeout(() => setCopied(false), 2000);
        };
        try {
            await navigator.clipboard.writeText(link);
            done();
        } catch {
            try {
                const ta = document.createElement('textarea');
                ta.value = link;
                ta.style.position = 'fixed';
                ta.style.opacity = '0';
                document.body.appendChild(ta);
                ta.select();
                document.execCommand('copy');
                document.body.removeChild(ta);
                done();
            } catch {
                toast.error('কপি করা যায়নি');
            }
        }
    }, [createdSub]);

    const regular = price?.originalPrice ?? 5000;
    const offerPrice = price?.finalPrice ?? 1000;
    const savings = price?.savings ?? regular - offerPrice;

    const FEATURES = [
        'ফ্রি সাব-ডোমেইন + কাস্টম ডোমেইন',
        'ফ্রি SSL ও সুপার ফাস্ট হোস্টিং',
        '৫০০ অর্ডার/মাস ও ১০০ প্রোডাক্ট',
        'বিকাশ ও ক্যাশ অন ডেলিভারি',
        'কুরিয়ার ইন্টিগ্রেশন ও রিপোর্ট',
    ];

    if (loading) {
        return (
            <div className={styles.page}>
                <div className={styles.loaderWrap}>
                    <span className={styles.spinner} />
                    <p>লোড হচ্ছে...</p>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.page}>
            <ToastContainer position="top-center" />

            <header className={styles.topbar}>
                <div className={styles.brand}>
                    <span className={styles.brandLogo}>
                        <img src={`${process.env.PUBLIC_URL}/logo512.png`} alt="ইকমটেক" />
                    </span>
                    <span className={styles.brandName}>ইকমটেক</span>
                </div>
                <button className={styles.plansLink} onClick={() => navigate('/billing')}>
                    সব প্ল্যান দেখুন →
                </button>
            </header>

            <main className={styles.wrap}>
                <section className={styles.hero}>
                    {!expired && <span className={styles.eyebrow}>🚀 একচেটিয়া লঞ্চ অফার</span>}
                    <h1 className={styles.title}>আপনার অনলাইন স্টোর আজই চালু করুন</h1>
                    <p className={styles.subtitle}>
                        {expired ? (
                            <>আপনার পছন্দের প্ল্যান বেছে নিন — সর্বনিম্ন <strong>৳৫০০/মাস</strong> থেকে শুরু।</>
                        ) : (
                            <>
                                স্টার্টআপ প্ল্যান — পুরো <strong>১ বছর</strong>, মাত্র{' '}
                                <strong>৳{bn(offerPrice.toLocaleString('en-US'))}</strong>। সাইন আপের ৪৮ ঘণ্টার মধ্যে।
                            </>
                        )}
                    </p>
                </section>

                <div className={styles.grid}>
                    {/* Video + free store creation in one card */}
                    <div className={styles.videoCard}>
                        {VIDEO_URL ? (
                            <div className={styles.videoFrame}>
                                <iframe
                                    src={VIDEO_URL}
                                    title="EcomTech"
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                />
                            </div>
                        ) : (
                            <div className={styles.videoPlaceholder} aria-label="ভিডিও শীঘ্রই আসছে">
                                <div className={styles.playBtn}>
                                    <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M8 5v14l11-7z" />
                                    </svg>
                                </div>
                                <p>কীভাবে কাজ করে — ভিডিও</p>
                            </div>
                        )}
                        <ul className={styles.videoPoints}>
                            <li>✓ ৩০ সেকেন্ডে স্টোর তৈরি</li>
                            <li>✓ ডোমেইন কানেক্ট + SSL অটো</li>
                            <li>✓ আজই অর্ডার নেওয়া শুরু</li>
                            <li>✓ কোনো কোডিং লাগবে না</li>
                            <li>✓ রেডিমেড থিম — এক ক্লিকে ডিজাইন</li>
                        </ul>

                        {/* Free store creation (inside the video card) */}
                        <div className={styles.storeInline}>
                            {!createdSub ? (
                                <>
                                    <h3 className={styles.storeTitle}>ফ্রি স্টোর তৈরি করুন</h3>
                                    <div className={styles.storeInputWrap}>
                                        <input
                                            type="text"
                                            lang="en"
                                            placeholder="যেমন: myshop"
                                            value={storeName}
                                            onChange={(e) => setStoreName(e.target.value)}
                                            className={
                                                storeName ? (storeValid ? styles.inputOk : styles.inputBad) : ''
                                            }
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' && storeValid) handleCreateStore();
                                            }}
                                        />
                                        {storeSub && (
                                            <span className={styles.urlPreview}>
                                                https://<strong>{previewSub}</strong>.ecomtechbd.com
                                            </span>
                                        )}
                                    </div>
                                    <button
                                        className={styles.storeBtn}
                                        onClick={handleCreateStore}
                                        disabled={!storeValid || creating}
                                    >
                                        {creating ? (
                                            <>
                                                <span className={styles.spinnerSm} /> তৈরি হচ্ছে…
                                            </>
                                        ) : (
                                            'স্টোর তৈরি করুন'
                                        )}
                                    </button>
                                    {storeName && storeMsg && <p className={styles.storeErr}>{storeMsg}</p>}
                                </>
                            ) : (
                                <div className={styles.storeCreated}>
                                    <div className={styles.createdHead}>
                                        <span className={styles.createdCheck}>✓</span>
                                        <span className={styles.createdTitle}>স্টোর তৈরি হয়েছে</span>
                                    </div>
                                    <div className={styles.createdUrl}>
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <circle cx="12" cy="12" r="10" />
                                            <line x1="2" y1="12" x2="22" y2="12" />
                                            <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
                                        </svg>
                                        <span>{createdSub}.ecomtechbd.com</span>
                                    </div>
                                    <div className={styles.createdActions}>
                                        <button type="button" className={styles.viewBtn} onClick={() => setShowPreview(true)}>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                                <circle cx="12" cy="12" r="3" />
                                            </svg>
                                            দেখুন
                                        </button>
                                        <button type="button" className={styles.copyBtn} onClick={handleCopyStore}>
                                            {copied ? (
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                    <polyline points="20 6 9 17 4 12" />
                                                </svg>
                                            ) : (
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                                                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                                                </svg>
                                            )}
                                            {copied ? 'কপি হয়েছে' : 'কপি'}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Offer */}
                    <div className={`${styles.offerCard} ${expired ? styles.offerExpired : ''}`}>
                        {!expired ? (
                            <>
                                <div className={styles.badgeRow}>
                                    <span className={styles.discountBadge}>
                                        {bn(price?.discount ?? 80)}% ছাড়
                                    </span>
                                    <span className={styles.limitedBadge}>সীমিত সময়</span>
                                </div>

                                <p className={styles.planName}>স্টার্টআপ • বার্ষিক</p>

                                <div className={styles.priceRow}>
                                    <span className={styles.price}>৳{bn(offerPrice.toLocaleString('en-US'))}</span>
                                    <span className={styles.regular}>৳{bn(regular.toLocaleString('en-US'))}</span>
                                    <span className={styles.per}>/বছর</span>
                                </div>
                                <p className={styles.savings}>
                                    আপনি বাঁচাচ্ছেন ৳{bn(savings.toLocaleString('en-US'))}
                                </p>

                                <div className={styles.countdown}>
                                    <span className={styles.countLabel}>অফার শেষ হতে বাকি</span>
                                    <div className={styles.timer}>
                                        <div className={styles.timeBox}>
                                            <strong>{bn(pad2(hh))}</strong>
                                            <small>ঘণ্টা</small>
                                        </div>
                                        <span className={styles.colon}>:</span>
                                        <div className={styles.timeBox}>
                                            <strong>{bn(pad2(mm))}</strong>
                                            <small>মিনিট</small>
                                        </div>
                                        <span className={styles.colon}>:</span>
                                        <div className={styles.timeBox}>
                                            <strong>{bn(pad2(ss))}</strong>
                                            <small>সেকেন্ড</small>
                                        </div>
                                    </div>
                                </div>

                                <ul className={styles.features}>
                                    {FEATURES.map((f) => (
                                        <li key={f}>
                                            <span className={styles.check}>✓</span> {f}
                                        </li>
                                    ))}
                                </ul>

                                <button
                                    className={styles.cta}
                                    onClick={handleBuy}
                                    disabled={paying}
                                >
                                    {paying ? (
                                        <>
                                            <span className={styles.spinnerSm} /> প্রসেসিং...
                                        </>
                                    ) : (
                                        <>🔒 এখনই কিনুন — ৳{bn(offerPrice.toLocaleString('en-US'))}</>
                                    )}
                                </button>
                                <p className={styles.trust}>১০০% নিরাপদ bKash পেমেন্ট • ২৪/৭ সাপোর্ট</p>
                            </>
                        ) : (
                            <>
                                <div className={styles.expiredIcon}>⏰</div>
                                <p className={styles.planName}>অফারের সময় শেষ</p>
                                <p className={styles.expiredText}>
                                    লঞ্চ অফারটি আর নেই। স্টোর চালু করতে একটি প্ল্যান কিনুন।
                                </p>
                                <button className={styles.cta} onClick={() => navigate('/billing')}>
                                    প্ল্যান কিনুন
                                </button>
                                <p className={styles.trust}>সব প্ল্যান ৳৫০০/মাস থেকে শুরু</p>
                            </>
                        )}
                        <button className={styles.plansLinkBottom} onClick={() => navigate('/billing')}>
                            সব প্ল্যান দেখুন →
                        </button>
                    </div>
                </div>

            </main>

            {/* In-page store preview */}
            {showPreview && createdSub && (
                <div className={styles.modalOverlay} onClick={() => setShowPreview(false)}>
                    <div className={styles.modalBox} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHead}>
                            <span className={styles.modalUrl}>{createdSub}.ecomtechbd.com</span>
                            <div className={styles.modalActions}>
                                <button
                                    type="button"
                                    className={styles.modalOpen}
                                    onClick={handleCopyStore}
                                >
                                    {copied ? (
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                            <polyline points="20 6 9 17 4 12" />
                                        </svg>
                                    ) : (
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                                        </svg>
                                    )}
                                    <span className={styles.copyText}>{copied ? 'কপি হয়েছে' : 'লিংক কপি'}</span>
                                </button>
                                <button
                                    className={styles.modalClose}
                                    onClick={() => setShowPreview(false)}
                                    aria-label="বন্ধ করুন"
                                >
                                    ✕
                                </button>
                            </div>
                        </div>
                        <iframe
                            className={styles.modalFrame}
                            src={`https://${createdSub}.ecomtechbd.com`}
                            title="Store preview"
                        />
                        <div className={styles.modalFoot}>
                            ⏳ স্টোর ৭ দিন লাইভ থাকবে — ৭ দিনের মধ্যে পেমেন্ট না করলে স্টোর মুছে যাবে।
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default GetStarted;
